class DurynLabResults extends HTMLElement {
  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    this.signal = this.abortController.signal;
    this.pageKey = this.dataset.pageKey || 'lab-results';
    this.activeCategory = 'all';
    this.cacheElements();
    this.bindEvents();
    this.applyFilters();
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  cacheElements() {
    const selectorKey = CSS.escape(this.pageKey);
    this.categorySections = [...document.querySelectorAll(`[data-lab-category-section][data-lab-page="${selectorKey}"]`)];
    this.groups = this.categorySections.map((section) => section.querySelector('.lab-results-group')).filter(Boolean);
    this.rows = this.groups.flatMap((group) => [...group.querySelectorAll('tbody tr')]);
    this.chips = this.querySelector('[data-lab-chips]');
    this.search = this.querySelector('[data-lab-search]');
    this.status = this.querySelector('[data-lab-status]');
    this.count = this.querySelector('[data-lab-count]');
    this.empty = this.querySelector('[data-lab-empty]');
    this.expand = this.querySelector('[data-lab-expand]');
    this.printButton = this.querySelector('[data-lab-print]');

    this.rows.forEach((row) => {
      row.dataset.searchText = this.normalize(row.textContent);
    });
  }

  bindEvents() {
    this.search?.addEventListener('input', () => this.applyFilters(), { signal: this.signal });
    this.status?.addEventListener('change', () => this.applyFilters(), { signal: this.signal });
    this.chips?.addEventListener('click', (event) => this.handleChipClick(event), { signal: this.signal });
    this.querySelectorAll('[data-lab-top-search]').forEach((button) => {
      button.addEventListener('click', () => this.applyTopSearch(button.dataset.labTopSearch), { signal: this.signal });
    });
    this.expand?.addEventListener('click', () => this.toggleAll(), { signal: this.signal });
    this.printButton?.addEventListener('click', () => window.print(), { signal: this.signal });
    window.addEventListener('beforeprint', () => this.openAllGroups(), { signal: this.signal });
    document.addEventListener('shopify:section:load', () => this.refreshContent(), { signal: this.signal });
  }

  handleChipClick(event) {
    const button = event.target.closest('[data-category]');
    if (!button || !this.chips.contains(button)) return;

    this.activeCategory = button.dataset.category;
    this.chips.querySelectorAll('[data-category]').forEach((chip) => {
      chip.setAttribute('aria-pressed', chip === button ? 'true' : 'false');
    });

    this.applyFilters();
    if (this.activeCategory !== 'all') {
      const group = this.groups.find((item) => item.dataset.category === this.activeCategory);
      if (group) {
        group.open = true;
        group.scrollIntoView({ block: 'start' });
      }
    }
  }

  applyTopSearch(term) {
    if (!this.search) return;
    this.search.value = term || '';
    this.activeCategory = 'all';
    this.chips?.querySelectorAll('[data-category]').forEach((chip) => {
      chip.setAttribute('aria-pressed', chip.dataset.category === 'all' ? 'true' : 'false');
    });
    this.applyFilters();
    this.search.focus();
  }

  applyFilters() {
    const term = this.normalize(this.search?.value.trim() || '');
    const status = this.status?.value || 'all';
    let shown = 0;

    this.groups.forEach((group) => {
      const categoryMatches = this.activeCategory === 'all' || group.dataset.category === this.activeCategory;
      let groupCount = 0;

      group.querySelectorAll('tbody tr').forEach((row) => {
        const matches =
          categoryMatches &&
          (status === 'all' || row.dataset.status === status) &&
          (!term || row.dataset.searchText.includes(term));
        row.hidden = !matches;
        if (matches) groupCount += 1;
      });

      group.hidden = groupCount === 0;
      const groupTotal = group.querySelector('[data-lab-group-count]');
      if (groupTotal) groupTotal.textContent = this.formatCount(groupCount);
      if (term && groupCount) group.open = true;
      shown += groupCount;
    });

    if (this.count) {
      this.count.textContent = `${this.dataset.showingLabel || ''} ${shown} ${this.dataset.ofLabel || ''} ${this.rows.length}`.trim();
    }
    if (this.empty) this.empty.hidden = shown !== 0;
  }

  formatCount(count) {
    const singular = this.dataset.itemSingular || '';
    const plural = this.dataset.itemPlural || '';
    return `${count} ${count === 1 ? singular : plural}`.trim();
  }

  toggleAll() {
    if (!this.expand) return;
    const shouldOpen = this.expand.getAttribute('aria-pressed') !== 'true';
    this.groups.filter((group) => !group.hidden).forEach((group) => {
      group.open = shouldOpen;
    });
    this.expand.setAttribute('aria-pressed', shouldOpen ? 'true' : 'false');
    this.expand.textContent = shouldOpen ? this.dataset.collapseLabel || '' : this.dataset.expandLabel || '';
  }

  openAllGroups() {
    this.groups.filter((group) => !group.hidden).forEach((group) => {
      group.open = true;
    });
  }

  normalize(value) {
    return String(value)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase();
  }

  refreshContent() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    this.signal = this.abortController.signal;
    this.cacheElements();
    this.bindEvents();
    this.applyFilters();
  }
}

if (!customElements.get('duryn-lab-results')) {
  customElements.define('duryn-lab-results', DurynLabResults);
}
